import {
  Avatar,
  Block,
  Button,
  Card,
  Divider,
  Grid,
  Heading,
  Icon,
  Input,
  Paragraph,
  RadioInput,
  SearchIcon,
  UserIcon,
} from '@deckee/deck-hand';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'throttle-debounce';
import { callEndpoint, telemetry } from '../../../../../../services/backend';
import { captureEvent } from '../../../../../../services/event-capture';
import { getStoredImage } from '../../../../../../services/image';
import { getMemberDisplayName } from '../../../../../../services/member/profile';
import { setShowingMenuBar } from '../../../../../../services/store/modules/app-controls';
import {
  setShareContact,
  setShareContactEmail,
  setStep,
} from '../../../../../../services/store/modules/float-plan';
import FloatPlanInviteContact from '../../../../FloatPlan/FloatPlanInviteContact';
import { TYPES } from '../../../../../shared/_deprecated/components/ControlButton/ControlButton';
import HeaderBar from '../../../../../shared/_deprecated/features/HeaderBar/HeaderBar';
import PageHolderContent from '../../../../../containers/PageHolderContent/PageHolderContent';
import FloatPlanStep from '../float-plan-step';

const ShareContactStep = () => {
  const dispatch = useDispatch();
  const context = useSelector((state) => state.floatPlan);
  const [lastSharedWith, setLastSharedWith] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [isInvitingContact, setInvitingContact] = React.useState(false);
  const [showingContinueButton, setShowingContinueButton] =
    React.useState(true);

  const debouncedSearchChange = useMemo(
    () =>
      debounce(400, async (event) => {
        const text = event.target.value;
        const trimmed = text.charAt(0) === '@' ? text.substring(1) : text;

        if (trimmed.length > 0) {
          const matches = await callEndpoint(
            callEndpoint.endpoints.searchForUsers,
            {},
            {
              search: trimmed,
            },
          );

          if (matches) {
            setMembers(matches);
          }
        }
      }),
    [],
  );

  const onSearchChange = useCallback(
    (event) => {
      setSearch(event.target.value);
      debouncedSearchChange(event);
    },
    [debouncedSearchChange],
  );

  const onFloatPlanShare = (member) => {
    // if the float-plan is being shared to an existing member set the contact as a member object
    if (member?.id) {
      dispatch(setShareContact(member));
      dispatch(setShareContactEmail(null));
    } else {
      // where the member does not exist just store the email the float-plan was shared to and reset any pre-selected contacts
      dispatch(setShareContact(null));
      dispatch(setShareContactEmail(member.email));
    }
  };

  const setFloatPlanStep = (step) => {
    dispatch(setStep(step));
  };

  // Hide the continue button and menubar when search is active
  const onSearchFocus = () => {
    dispatch(setShowingMenuBar(false));
    setShowingContinueButton(false);
  };

  // Show the continue button and menubar when search is inactive
  const onSearchBlur = () => {
    dispatch(setShowingMenuBar(true));
    setShowingContinueButton(true);
  };

  React.useEffect(() => {
    let isMounted = true;

    const getLastSharedWith = async () => {
      try {
        const { getFloatPlanLastSharedWith: data } = await callEndpoint(
          callEndpoint.endpoints.getFloatPlanLastSharedWith,
        );

        telemetry(
          telemetry.DEBUG,
          `[ShareContactStep.getLastSharedWith] getting trip plan details: ${JSON.stringify(
            data,
          )}`,
        );

        if (isMounted) {
          setLastSharedWith(data);
          setMembers(data);
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[ShareContactStep.getLastSharedWith] Error retrieving ${JSON.stringify(
            err,
          )}`,
        );
      }
    };

    dispatch(setShowingMenuBar(true));
    getLastSharedWith();

    return () => {
      isMounted = false;
      telemetry(telemetry.DEBUG, '[ShareContactStep.useEffect] unmounted');
    };
  }, [dispatch]);

  const handleSelectMemberOnClick = async (member) => {
    await captureEvent({
      type: 'float: designate contact',
      payload: {
        selection: search.length === 0 ? 'last used' : 'searched',
      },
      sendToAmplitude: true,
    });

    onFloatPlanShare(member);
  };

  const handleInviteGuestOnClick = async (member) => {
    await captureEvent({
      type: 'float: designate contact',
      payload: {
        selection: 'invited',
      },
      sendToAmplitude: true,
    });

    onFloatPlanShare(member);
    setFloatPlanStep(FloatPlanStep.CREATE);
  };

  // load back in last shared members if there is nothing in the search bar
  React.useEffect(() => {
    if (search.length === 0) {
      setMembers(lastSharedWith);
    }
  }, [lastSharedWith, search]);

  return (
    <PageHolderContent
      headerBarContent={
        <HeaderBar
          leftType={TYPES.RETURN}
          leftAction={() => setFloatPlanStep(FloatPlanStep.CREATE)}
          centerNode="Select contact"
        />
      }
    >
      <Block
        display="flex"
        flexDirection="column"
        height="100%"
        maxHeight="100%"
      >
        <Block m="10px">
          <Input
            aria-label="search for deckee profile"
            type="text"
            placeholder="Search for a Deckee profile"
            startEnhancer={() => <SearchIcon color="#FF5C39" />}
            value={search}
            onChange={onSearchChange}
            clearable
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
          />
        </Block>

        <Block mb="10px" overflowY="auto" flex="1 1 auto" as="form">
          {lastSharedWith.length > 0 && search.length === 0 && (
            <Grid my="10px">
              <Grid.Column>
                <Heading level={6}>Last Used</Heading>
              </Grid.Column>
            </Grid>
          )}

          {members.length > 0 && (
            <Grid>
              {members.map((member) => (
                <Grid.Column key={member.id} span={[4, 8, 12]} gaps={0}>
                  <Divider />

                  <Block>
                    <Card
                      bg="transparent"
                      onClick={() => handleSelectMemberOnClick(member)}
                    >
                      <Card.Head>
                        <Card.Thumbnail>
                          {member.image != null && (
                            <Avatar src={getStoredImage(member.image, 40)} />
                          )}

                          {member.image == null && (
                            <Icon.Circle>
                              <UserIcon color="#1a1a1a" />
                            </Icon.Circle>
                          )}
                        </Card.Thumbnail>

                        <Card.DeprecatedHeading>
                          <Paragraph lines={1}>
                            {`@${getMemberDisplayName(member)} - ${
                              member.title
                            }`}
                          </Paragraph>
                        </Card.DeprecatedHeading>

                        <Card.Thumbnail>
                          <RadioInput
                            name={`e2e-${member.id}`}
                            value={member.id}
                            checked={context.share_contact?.id === member.id}
                            onChange={() => onFloatPlanShare(member)}
                          />
                        </Card.Thumbnail>
                      </Card.Head>
                    </Card>
                  </Block>
                </Grid.Column>
              ))}
            </Grid>
          )}
        </Block>

        {showingContinueButton && (
          <Block mx="10px" mb="10px">
            <Heading
              color="tertiary"
              textAlign="center"
              level={6}
              onClick={() => {
                dispatch(setShowingMenuBar(false));
                setInvitingContact(true);
              }}
            >
              Invite a new contact
            </Heading>

            <Button
              disabled={context.share_contact == null}
              onClick={() => setFloatPlanStep(FloatPlanStep.CREATE)}
              wide
            >
              Continue
            </Button>
          </Block>
        )}
      </Block>

      {isInvitingContact && (
        <FloatPlanInviteContact
          onSubmit={handleInviteGuestOnClick}
          onClose={() => {
            dispatch(setShowingMenuBar(true));
            setInvitingContact(false);
          }}
        />
      )}
    </PageHolderContent>
  );
};

export default ShareContactStep;
